import { ChangeDetectorRef, Component, ElementRef, HostListener, QueryList, signal, ViewChild, ViewChildren, } from '@angular/core';

import {
  DirectionsRendererData,
  MapContextMenuEntry,
  plantIcon,
  QPointMapMode,
  QPointMapModeConfiguration,
  siteBeginIcon,
  siteEndIcon,
  vehicleLoadedIcon,
  MapComponent as QPointMapComponent,
  MapPlantSuggestlistComponent,
  FocusEnum,
  MapMarkerComponent,
  MapMarkerLabelDirective
} from '../../../projects/qpoint-maps/src/public_api';
import { NgFor, NgIf, NgTemplateOutlet } from '@angular/common';
import { Align, Offset, PopupModule } from '@progress/kendo-angular-popup';
import { QPointButtonComponent } from '@qpoint/buttons';
import { timer } from 'rxjs';
import { DropDownVariant } from '@qpoint/forms';
import { IFocusElement } from '@qpoint/maps/lib/components/map/focus-element';
import { MapDirectionComponent } from "../../../projects/qpoint-maps/src/lib/components/map/map-direction/map-direction.component";

class GeoPointOption {
  icon: google.maps.Icon;
  label: string;
}

export enum GeoPointType {
  SiteBegin = 'SiteBegin',
  SiteEnd = 'SiteEnd',
  SiteEntrance = 'SiteEntrance',
  SiteExit = 'SiteExit',
  BreakSpace = 'BreakSpace',
  CleaningSpace = 'CleaningSpace',
  GridDepot = 'GridDepot',
  Material = 'Material',
  TurningPlace = 'TurningPlace',
  WaitingSpace = 'WaitingSpace',
  WaterPlace = 'WaterPlace',
  Waypoint = 'Waypoint',
  AssemblyLocation = 'AssemblyLocation',
  MixingPlant = 'MixingPlant',
  Vehicle = 'Vehicle',
  SitePosition = 'SitePosition',
}

export interface IPoi {
  position?: {
    lat?: number;
    lng?: number;
  }
  icon: google.maps.Icon;
  type: GeoPointType;
  draggable?: boolean;
  readonly?: boolean;
  label?: string;
  labelAddendum?: string;
  labelSecondRow?: string;
  tooltipData?: any;
  fitToBounds?: boolean;
  address?: string;
  notVisible?: boolean;
}

interface IDummyItem {
  key: string;
  value: string;
  id: string;
  name?: string;
}

@Component({
    selector: 'app-map-test',
    templateUrl: './map-test.component.html',
    styleUrls: ['./map-test.component.scss'],
    imports: [
        NgFor,
        NgIf,
        PopupModule,
        QPointButtonComponent,
        NgTemplateOutlet,
        MapPlantSuggestlistComponent,
        MapDirectionComponent,
        MapMarkerComponent,
        MapMarkerLabelDirective,
        QPointMapComponent
    ]
})
export class MapTestComponent {
  focusChangedType = FocusEnum;
  showOverlay = false;
  showSelector = signal(false);
  showZoomButton = true;
  showFitToBoundsButton = true;
  enableFullscreenButton = signal(true);
  currentActionGeoPoint;
  geoPointOptions: GeoPointOption[] = [];
  overlayPosition: { latitude: number, longitude: number };
  editMarker = true;
  initialLoading = true;
  popupOffset: Offset;
  anchorAlign: Align = { horizontal: 'right', vertical: 'bottom' };
  popupAlign: Align = { horizontal: 'right', vertical: 'top' };
  itemsSignal = signal<IDummyItem[]>([]);
  zoomPosition: google.maps.ControlPosition = google.maps.ControlPosition.RIGHT_BOTTOM;
  mapTypePosition: google.maps.ControlPosition = google.maps.ControlPosition.TOP_RIGHT;
  localStorageKey = '4d3ea599-fa92-44d5-bcbc-e3cee117528a';
  showFocusButton = true;
  siteBeginPoi = null;
  focusElements: IFocusElement[] = [
    { identifier: FocusEnum.Site, label: 'Baustelle' } as IFocusElement,
    { identifier: FocusEnum.Overview, label: 'Übersicht' } as IFocusElement,
    { identifier: FocusEnum.Plant, label: 'Plant 1 langer Namen', id: 'FBC3FCE3-122F-EB11-9FB4-000D3A20FA32' } as IFocusElement,
    { identifier: FocusEnum.Plant, label: 'Plant 2', id: 'C9C40BEA-75E3-ED11-9F74-000D3A214CF3' } as IFocusElement,
  ];

  additionalMarkerOptions$ = signal<IPoi[]>([]);
  additionalMarkerOptions: IPoi[] = [];
  isInEditMode = false;
  mapModes: QPointMapModeConfiguration[] = [
    {
      mode: QPointMapMode.Polygon,
      multi: false,
      active: false,
      tooltip: 'test 123',
      color: '#219EBC',
    },
    {
      mode: QPointMapMode.Polyline,
      multi: false,
      active: false,
      tooltip: 'test 123',
      icon: 'arrow-right',
      scope: 'all',
      color: '#8993F8',
    },
    {
      mode: QPointMapMode.Polyline,
      multi: false,
      active: false,
      tooltip: 'test 123',
      icon: 'arrow-down',
      scope: 'test',
      disabled: false,
      color: ' #DB983A',
    }
  ];
  mapContextMenu: MapContextMenuEntry[] = [];

  directionData = DirectionsRendererData.result;

  @ViewChild('popup', { read: ElementRef })
  popup: ElementRef;

  @ViewChild('qpointMap')
  qpointMap: QPointMapComponent;

  @ViewChildren(MapMarkerComponent) mapMarkers!: QueryList<MapMarkerComponent>;

  @HostListener('document:click', ['$event'])
  documentClick(event: any): void {
    if (!(this.popup ? this.popup.nativeElement.contains(event.target) : false) && this.showOverlay) {
      this.showOverlay = false;
    }
  }

  dropDownVariant = DropDownVariant;

  constructor(private cdr: ChangeDetectorRef) {
    const items = [];
    for (let i = 0; i < 50; i++) {
      items.push({
        key: `item-${i}`,
        value: `This is item ${i}`,
        id: `${i}`,
        name: `My name is ${i}`
      });
    }
    this.itemsSignal.set(items);
  }

  toggleSelector() {
    this.showSelector.set(!this.showSelector())
  }

  onBoundsChanged($event: google.maps.LatLngBounds) {
    console.log('Bounds changed', $event);
  }

  toggleEnableFullScreenButton() {
    this.enableFullscreenButton.set(!this.enableFullscreenButton());
  }

  handlePlantChangedEvent(event: any) {
    console.log('Event', event);
  }

  public getZIndexByPoiType(type: GeoPointType): number {
    if (type === GeoPointType.Vehicle) {
      return Number.MAX_SAFE_INTEGER;
    }
    return null;
  }

  addTestLabels() {
    this.additionalMarkerOptions$.set(this.additionalMarkerOptions$().map(o => {
      o.label = 'Test';
      o.labelAddendum = 'Test';
      o.labelSecondRow = 'Test';
      return o;
    }));
  }

  mapLoaded($event: google.maps.Map) {
    console.log('Map loaded', $event);
    timer(1).subscribe(() => {
      this.siteBeginPoi = {
        position: {
          lat: 46.50307594,
          lng: 16.22888167
        },
        type: GeoPointType.SiteBegin,
        icon: siteBeginIcon(),
        draggable: true,
        fitToBounds: true
      };
      this.additionalMarkerOptions$.set([
        this.siteBeginPoi,
        {
          position: {
            lat: 46.69407594,
            lng: 16.11388167
          },
          type: GeoPointType.MixingPlant,
          icon: plantIcon(),
          draggable: true,
          fitToBounds: true
        },
        {
          position: {
            lat: 46.88 + Math.random() * 0.1,
            lng: 16.10 + Math.random() * 0.1
          },
          type: GeoPointType.Vehicle,
          icon: vehicleLoadedIcon(),
          fitToBounds: true
        },
        {
          position: {
            lat: 45.88 + (Math.random() + 1) * 0.1,
            lng: 15.10 + (Math.random() + 1) * 0.1
          },
          type: GeoPointType.Vehicle,
          icon: vehicleLoadedIcon(),
          fitToBounds: true
        }]);
      this.additionalMarkerOptions = this.additionalMarkerOptions$();
      this.addTestLabels();

      // for (let i = 0; i < 30; i++) {
      //   this.additionalMarkerOptions.push({
      //     position: {
      //       lat: 46.88 + Math.random(),
      //       lng: 16.10 + Math.random()
      //     },
      //     icon: constructionSiteIcon(),
      //     fitToBounds: true
      //   });
      // }
      this.initialLoading = false;
      // let time = 0;
      // interval(5 * 1000).subscribe(() => {
      //   if (time < 5) {
      //     time++;
      //   }
      //   this.additionalMarkerOptions = [{
      //     position: {
      //           lat: 46.50307594 + time * 0.02,
      //           lng: 16.22888167
      //         },
      //     icon: vehicleLoadedIcon(),
      //     draggable: true,
      //     fitToBounds: true
      //   }];
      //   this.initialLoading = false;
      // });

      // interval(15 * 1000).subscribe(() => {
      //   this.additionalMarkerOptions.splice(this.additionalMarkerOptions.findIndex(o => !o.fitToBounds), 1, {
      //     position: {
      //       lat: 46.18 + Math.random() * 0.7,
      //       lng: 16 + Math.random() * 0.7
      //     },
      //     icon: vehicleLoadedIcon(),
      //     fitToBounds: false
      //   });
      // });
    });

    this.qpointMap.mapModeChanged.subscribe((mode) => {
      console.log('Map mode changed', mode);
    });
  }

  mapRightClick(lat: number, lng: number) {
    console.log('mapRightClick', lat, lng);
    this.currentActionGeoPoint = null;
    this.showOverlay = true;
    this.overlayPosition = { longitude: lng, latitude: lat };
    this.geoPointOptions = this.createGeoPointOptions();
  }

  markerRightClicked($event) {
    console.log('Marker right clicked', $event);
    console.log($event.latLng.lat(), $event.latLng.lng());
    this.currentActionGeoPoint = $event;
    this.showOverlay = true;
  }

  rightClick($event: MouseEvent) {
    console.log('Right click', $event);
    this.popupOffset = { left: $event.clientX, top: $event.clientY };
    this.popupOffset.left = $event.clientX;
    this.popupOffset.top = $event.clientY;
    $event.preventDefault();
    $event.stopPropagation();
  }

  geoPointOptionTriggered(option: GeoPointOption) {
    console.log('GeoPoint option triggered', option, this.overlayPosition);
    this.additionalMarkerOptions$.set([...this.additionalMarkerOptions$(), {
      position: { lat: this.overlayPosition.latitude, lng: this.overlayPosition.longitude },
      type: GeoPointType.SiteExit,
      icon: option.icon,
      draggable: true,
      fitToBounds: true
    }]);
    this.showOverlay = false;
  }

  deleteCurrentPoi() {
    console.log('delete', this.additionalMarkerOptions$().map(o => o.position), this.currentActionGeoPoint.latLng.lat(), this.currentActionGeoPoint.latLng.lng());
    const spliced = this.additionalMarkerOptions$().splice(this.additionalMarkerOptions$().findIndex(o => o.position.lat === this.currentActionGeoPoint.latLng.lat() && o.position.lng === this.currentActionGeoPoint.latLng.lng()), 1);
    this.additionalMarkerOptions$.set(spliced);
    this.currentActionGeoPoint = null;
    this.showOverlay = false;
  }

  private createGeoPointOptions(): GeoPointOption[] {
    const geoPointOptions: GeoPointOption[] = [];
    geoPointOptions.push(
      {
        label: 'project.map.siteBegin',
        icon: siteBeginIcon()
      },
      {
        label: 'project.map.siteEnd',
        icon: siteEndIcon()
      }
    );
    return geoPointOptions;
  }

  onSubmit() {
    console.log('Form submitted!');
  }

  filterPois(type?: GeoPointType): void {
    if (!type) {
      this.additionalMarkerOptions$.set(this.additionalMarkerOptions);
    }
    this.additionalMarkerOptions$.set(this.additionalMarkerOptions.filter(p => p.type != type));
  }

  focusChanged($event: FocusEnum) {
    switch ($event) {
      case FocusEnum.Site:
        this.filterPois(GeoPointType.Vehicle);
        this.qpointMap.setZoom(18);
        this.qpointMap.setCenter({ lat: this.siteBeginPoi?.position.lat, lng: this.siteBeginPoi?.position.lng });
        break;
      case FocusEnum.Overview:
        this.qpointMap.manualFitToBounds();
        this.filterPois(GeoPointType.SiteBegin);
    }
    console.log('Focus changed', $event);
  }

  printToConsole() {
    console.log("--- Icon Template Button Clicked ---")
  }

  openFirstInfoWindow() {
    console.log("--- openFirstInfoWindow: ", this.mapMarkers.toArray());
    // get random marker
    const random = this.mapMarkers.first;

    if(random) {
      random.openInfoWindow();
    }
  }

  closeFirstInfoWindow() {
    console.log("--- closeFirstInfoWindow: ", this.mapMarkers.toArray());

    const first = this.mapMarkers.first;

    if(first) {
      first.closeInfoWindow();
    }
  }

  shuffleMarkers() {
    console.log("Shuffle markers");
    this.additionalMarkerOptions$.update(markers => {
      const shuffled = markers.sort(() => Math.random() - 0.5);
      return shuffled;
    })
  }
}
